<script setup lang="ts">
import type { ElTooltipProps } from 'element-plus'

defineOptions({
  name: 'TableOverflowText',
})

const props = withDefaults(defineProps<{
  text?: string | number | null
  tooltip?: string | number | null
  lines?: 1 | 2
  tag?: boolean
  inline?: boolean
  alwaysTooltip?: boolean
  fallback?: string
  placement?: ElTooltipProps['placement']
}>(), {
  text: '',
  tooltip: null,
  lines: 1,
  tag: false,
  inline: false,
  alwaysTooltip: false,
  fallback: '-',
  placement: 'top',
})

const slots = useSlots()
const triggerRef = ref<HTMLElement>()
const isOverflowing = ref(false)

// 表格空值统一显示占位符，避免 null/undefined 被直接渲染出来。
const displayText = computed(() => {
  if (props.text === null || props.text === undefined || props.text === '') {
    return props.fallback
  }

  return String(props.text)
})

const tooltipText = computed(() => {
  if (props.tooltip !== null && props.tooltip !== undefined && props.tooltip !== '') {
    return String(props.tooltip)
  }

  return displayText.value
})

// 富内容提示通过 tooltip 插槽传入，普通文本提示继续使用 tooltip/text 属性。
const hasTooltipContent = computed(() => Boolean(slots.tooltip || tooltipText.value))

// 同时检查横向和纵向溢出，兼容单行、双行及 ElTag 内容。
function checkOverflow() {
  nextTick(() => {
    const trigger = triggerRef.value
    if (!trigger) {
      isOverflowing.value = false
      return
    }

    const targets = [trigger, ...trigger.querySelectorAll<HTMLElement>('*')]
    isOverflowing.value = targets.some(target =>
      (target.clientWidth > 0 && target.scrollWidth > target.clientWidth + 1)
      || (target.clientHeight > 0 && target.scrollHeight > target.clientHeight + 1),
    )
  })
}

let resizeObserver: ResizeObserver | undefined

// 列宽会随窗口和表格布局变化，使用 ResizeObserver 及时更新 Tooltip 状态。
onMounted(() => {
  checkOverflow()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(checkOverflow)
    if (triggerRef.value) {
      resizeObserver.observe(triggerRef.value)
    }
  }
})

onBeforeUnmount(() => resizeObserver?.disconnect())

watch(() => [props.text, props.tooltip, props.lines, props.tag, props.inline], checkOverflow)
</script>

<template>
  <ElTooltip
    :content="tooltipText"
    :disabled="!hasTooltipContent || (!alwaysTooltip && !isOverflowing)"
    :placement="placement"
    effect="light"
    :show-after="300"
  >
    <template v-if="$slots.tooltip" #content>
      <slot name="tooltip" />
    </template>
    <span
      ref="triggerRef"
      class="unified-table-overflow-text"
      :class="[
        `unified-table-overflow-text--${lines}-line`,
        {
          'unified-table-overflow-text--tag': tag,
          'unified-table-overflow-text--inline': inline,
        },
      ]"
    >
      <slot :text="displayText">
        {{ displayText }}
      </slot>
    </span>
  </ElTooltip>
</template>

<style scoped>
/* 所有长文本模式共用的尺寸和断词基础规则。 */
.unified-table-overflow-text {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  line-height: 1.45;
  text-align: inherit;
  overflow-wrap: anywhere;
}

/* ID、编号、URL、邮箱等字段固定为单行省略。 */
.unified-table-overflow-text--1-line {
  display: block;
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 名称、标题、备注、描述等字段最多显示两行。 */
.unified-table-overflow-text--2-line {
  display: -webkit-box;
  max-height: 2.9em;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

/* Tag 模式保持原有行内布局，不强制占满整个单元格。 */
.unified-table-overflow-text--tag {
  display: inline-flex;
  width: auto;
  vertical-align: middle;
}

/* 复合单元格原本通过 br 换行时使用行内模式，避免额外产生空白行。 */
.unified-table-overflow-text--inline {
  display: inline-block;
  width: auto;
  vertical-align: middle;
}

/* 行内双行文本仍保留两行截断能力。 */
.unified-table-overflow-text--inline.unified-table-overflow-text--2-line {
  display: -webkit-inline-box;
}

/* 限制 ElTag 自身宽度，避免撑开表格列。 */
.unified-table-overflow-text--tag :deep(.el-tag) {
  min-width: 0;
  max-width: 100%;
}

/* ElTag 内部文本统一单行省略，完整内容由 ElTooltip 展示。 */
.unified-table-overflow-text--tag :deep(.el-tag__content) {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
